import type { ComponentProps, ContextType, JSX } from 'preact';
import { useCallback, useRef, useState } from 'preact/hooks';

import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { merge } from '@oracle/oraclejet-internal-utilities/stringUtils';

import { detectTruncation } from '#utils/PRIVATE_truncationUtils';
import type { FormVariantContext } from '#hooks/UNSAFE_useFormVariantContext';
import type { Label } from '#UNSAFE_Label';
import type { TextField } from '#UNSAFE_TextField';
import type { InlineUserAssistance, UserAssistanceDensityType } from '#UNSAFE_UserAssistance';
import { useMessageSeverity } from '../PRIVATE_useMessageSeverity';
import { useFocusWithin } from '../UNSAFE_useFocusWithin';
import { useFormFieldContextProps } from '../UNSAFE_useFormFieldContextProps';
import { useId } from '../UNSAFE_useId';

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;
type PickedUserAssistanceProps = Pick<InlineUserAssistanceProps, 'messages'>;

type UseTextFieldProps<V> = PickedUserAssistanceProps & {
  ariaDescribedBy?: string;
  contentVariant?: 'input' | 'textarea';
  helpSourceLink?: string;
  helpSourceText?: string;
  isDisabled?: boolean;
  isFocused?: boolean;
  isLoading?: boolean;
  isReadonly?: boolean;
  isRequiredShown?: boolean;
  labelEdge?: ComponentProps<typeof TextField>['labelEdge'] | 'none';
  styleVariant?: ContextType<typeof FormVariantContext>;
  userAssistanceDensity?: UserAssistanceDensityType;
  value?: V;
};

/**
 * A custom hook to determine the props for a component that renders
 * a text field
 */
export function useTextField<V>({
  ariaDescribedBy,
  contentVariant,
  helpSourceLink,
  helpSourceText,
  isDisabled,
  isFocused,
  isLoading,
  isReadonly,
  isRequiredShown,
  labelEdge,
  messages,
  styleVariant,
  userAssistanceDensity,
  value
}: UseTextFieldProps<V>) {
  const baseId = useId();
  const labelId = labelEdge !== 'none' ? `${baseId}-label` : undefined;
  const inputId = !isReadonly ? `${baseId}-input` : undefined;
  const uaId = !isDisabled ? `${baseId}-ua` : undefined;
  const helpIconId = userAssistanceDensity === 'compact' ? `${baseId}-helpIcon` : undefined;

  // JET-73197: Truncated Text Enhancements for Core Pack
  // Create a ref to the Label element. When we see focusWithin the input, measure the label
  // to determine if the text is truncated and if so, set showTruncatedTooltip to true.
  // If we see a blurWithin event and the input is no longer focused, set the flag to false.
  const labelRef = useRef<HTMLLabelElement>(null);
  const [showTruncatedTooltip, setShowTruncatedTooltip] = useState(false);
  const onBlurWithin = useCallback(() => {
    setShowTruncatedTooltip(false);
  }, []);
  const onFocusWithin = useCallback(() => {
    const isTruncated = labelEdge === 'inside' && !isDisabled ? detectTruncation(labelRef) : false;
    setShowTruncatedTooltip(isTruncated);
  }, [isDisabled, labelEdge, labelRef]);

  const { focusProps, isFocused: isInputFocused } = useFocusWithin({
    isDisabled,
    onBlurWithin,
    onFocusWithin
  });
  const hasValue = value !== undefined && (typeof value !== 'string' || value !== '');

  // Create the form field context
  const formFieldContext = useFormFieldContextProps({
    hasTruncatedLabel: showTruncatedTooltip,
    hasValue,
    isDisabled,
    isFocused,
    isInputFocused,
    isLoading,
    isReadonly
  });

  // Determine the highest severity from the messages
  const messageSeverity = useMessageSeverity(messages);

  // Determine the label variant
  const labelVariant: ComponentProps<typeof Label>['variant'] =
    labelEdge !== 'none' ? labelEdge : undefined;

  return {
    baseId,
    formFieldContext,
    inputProps: {
      id: inputId,
      'aria-describedby': merge([helpIconId, uaId, ariaDescribedBy]),
      'aria-invalid':
        messageSeverity === 'error'
          ? 'true'
          : (undefined as UnpackSignals<JSX.AriaAttributes>['aria-invalid']),
      variant: styleVariant,
      onBlur: focusProps?.onFocusOut,
      onFocus: focusProps?.onFocusIn
    },
    labelProps: {
      forId: inputId,
      helpIconId,
      helpSourceLink,
      helpSourceText,
      id: labelId,
      isRequiredShown,
      labelRef,
      userAssistanceDensity,
      variant: labelVariant
    },
    textFieldProps: {
      contentVariant,
      statusVariant: /error|warning/.test(messageSeverity ?? '')
        ? (messageSeverity as 'error' | 'warning')
        : undefined,
      styleVariant
    },
    userAssistanceProps: {
      id: uaId
    }
  };
}
